import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Clock, ArrowUpRight, ShieldCheck, ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { trackEvent } from "@/utils/track";
import { usePageMeta } from "@/hooks/usePageMeta";

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);
  usePageMeta("Payment Received | Heartland Physics", "Your shielding design payment was received.");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
        if (!res.ok) throw new Error("not found");
        const data = await res.json();
        if (data.payment_status === "paid") {
          trackEvent("payment_confirmed");
          setState("paid");
          return;
        }
      } catch {
        // keep polling briefly
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        timer = setTimeout(poll, 2000);
      } else {
        setState("pending");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <section data-testid="payment-success-page" className="border-b border-border bg-blueprint-grid pt-16">
      <div className="mx-auto max-w-3xl px-5 py-20 md:px-8 md:py-28">
        <Reveal y={12}>
          <p className="eyebrow">Payment</p>
        </Reveal>
        <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
          <MaskedLines delay={0.1} lines={["Payment received."]} />
        </h1>

        {state === "checking" && (
          <Reveal delay={0.3}>
            <div className="mt-10 flex items-center gap-4 border border-border bg-white p-7" data-testid="payment-confirming">
              <Clock size={22} className="animate-pulse text-blueprint" />
              <p className="text-sm leading-relaxed text-ink/70">Confirming your payment with Stripe — this takes a few seconds…</p>
            </div>
          </Reveal>
        )}

        {state === "paid" && (
          <Reveal delay={0.3}>
            <div className="mt-10 border border-blueprint/30 bg-white p-8" data-testid="payment-confirmed">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={24} className="text-blueprint" />
                <p className="font-heading text-lg font-extrabold tracking-tight text-ink">Confirmed — your payment went through.</p>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-ink/70">
                One step left: complete the short intake form — facility, per-room details, and floor plans — so work can begin.
                A copy of this link was also sent to your email.
              </p>
              <Link
                to={`/request?session_id=${sessionId}`}
                data-testid="payment-success-continue-intake"
                className="group mt-7 inline-flex items-center gap-2 bg-blueprint px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink"
              >
                Continue your intake form
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <p className="mt-6 flex items-center gap-2 border-t border-border pt-5 text-xs text-ink/55">
                <ShieldCheck size={14} className="text-blueprint" />
                Covered by the 90-day money-back guarantee and 5 years of free recalculations.
              </p>
            </div>
          </Reveal>
        )}

        {(state === "pending" || state === "error") && (
          <Reveal delay={0.3}>
            <div className="mt-10 border border-border bg-white p-7" data-testid="payment-pending">
              <p className="text-sm leading-relaxed text-ink/70">
                We're still confirming this payment. If you completed checkout, it will register shortly — otherwise email{" "}
                <a href="mailto:Lancer@heartlandphysics.com" className="font-semibold text-blueprint">Lancer@heartlandphysics.com</a>{" "}
                with your receipt and we'll take it from there.
              </p>
              <Link to="/request" data-testid="payment-pending-back" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-blueprint">
                Back to the request page <ArrowUpRight size={15} />
              </Link>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default PaymentSuccess;
